/**
 * Apple 5.1.2(i): explicit permission before learner data reaches a third-party
 * AI (CMP-01, CMP-02, CMP-03).
 *
 * WHO FORCES THIS, stated because the last requirement built here turned out to
 * be forced by nobody. Guideline 5.1.2(i) verbatim: "you may not use, transmit,
 * or share someone's personal data without first obtaining their permission...
 * You must clearly disclose where personal data will be shared with third
 * parties, including with third-party AI, and obtain explicit permission before
 * doing so." Convo transmits learner text and audio to OpenAI and Anthropic on
 * the tutoring path. Without this, a first submission is rejected.
 *
 * WHAT IT DELIBERATELY DOES NOT CLAIM. The screen names the recipients and says
 * the data is not used to train their models. It makes NO claim about a
 * processing country: no guideline asks for one, and nothing in the provider
 * configuration pins a region, so the claim would have been false. The
 * requirement said otherwise until CMP-02 was amended on 2026-09-06; the
 * international-transfer fact belongs in the privacy policy, where it can be
 * stated honestly.
 *
 * THE NO-TRAINING CLAIM IS THE VENDORS', NOT OURS. Both state that API data is
 * not used for training by default. That is vendor policy rather than a control
 * this system exercises, and the copy attributes it accordingly.
 */

/**
 * Bumped when the DISCLOSED SET changes - a new recipient, one swapped for
 * another, or a materially different use.
 *
 * Permission is for what was disclosed. A consent taken against a two-provider
 * list is not permission for a third, and the version is what makes a stale
 * consent visible instead of silently counting.
 */
export const AI_CONSENT_VERSION = 1;

/**
 * The recipients the screen must name, in the order it names them.
 *
 * A LIST RATHER THAN PROSE, so the consent screen and any coverage check read
 * the same source. Adding a provider here without bumping the version above is
 * the mistake this pairing exists to make visible.
 */
export const AI_RECIPIENTS = ["OpenAI", "Anthropic"] as const;

export type AiConsentState = {
  /** Null when the learner has never been asked. */
  consentedAt: Date | null;
  /** The version consented to, which may be older than the current one. */
  version: number | null;
  /** CMP-03's optional second control. Null is "never asked", false is "declined". */
  tutorMemory: boolean | null;
};

/**
 * Whether the learner has given permission for the CURRENT disclosed set.
 *
 * A consent at an older version is NOT permission: it was given against a
 * different list of recipients, which is the whole reason the version is
 * stored. Re-asking is the correct response, not silently upgrading it.
 */
export function hasCurrentAiConsent(state: AiConsentState): boolean {
  return state.consentedAt !== null && state.version === AI_CONSENT_VERSION;
}

/**
 * Whether durable tutor memory may be written for this learner.
 *
 * Requires BOTH: the required consent, because memory is built from processed
 * conversation, and the optional one. A learner who consented to processing and
 * declined memory has said two different things and both are answers.
 */
export function mayWriteTutorMemory(state: AiConsentState): boolean {
  return hasCurrentAiConsent(state) && state.tutorMemory === true;
}
